import type { ReadOnlyResourceAdapter } from './media.ts';

/** The session id scopes reads; authorization remains in the server's
 * administrator session. Invalid or absent scopes never fall back to user APIs. */
export function inspectionResourceBase(id: string | undefined): string {
  if (typeof id !== 'string' || !/^[A-Za-z0-9_-]{16,128}$/.test(id)) throw new Error('只读查看已失效');
  return `/api/admin/inspections/${encodeURIComponent(id)}`;
}

export interface InspectionResources extends ReadOnlyResourceAdapter { request: typeof fetch }
export function inspectionResources(id: string | undefined, origin: string, request: typeof fetch = fetch.bind(globalThis), onAccessDenied?: (message:string)=>void): InspectionResources {
  let accessEnded=false;
  const denied=(status:number)=>[401,403,410].includes(status);
  const read=(async(input:RequestInfo|URL,init?:RequestInit)=>{
    if(accessEnded)throw new Error('只读查看已结束');
    const base=inspectionResourceBase(id),url=new URL(typeof input==='string'||input instanceof URL?input:input.url,origin);
    if(url.origin!==origin||url.username||url.password||url.hash||!url.pathname.startsWith(base+'/')
      ||init?.method!==undefined&&init.method!=='GET'||init?.body!==undefined)throw new Error('只读资源请求无效');
    const response=await request(url.href,{method:'GET',credentials:'same-origin',cache:'no-store',signal:init?.signal});
    let lost=denied(response.status);
    // A missing file is not a lost viewing session. The server intentionally
    // masks a different administrator session as 404, so verify the scope once.
    if(response.status===404&&onAccessDenied){const current=await request(base,{method:'GET',credentials:'same-origin',cache:'no-store',signal:init?.signal});lost=denied(current.status)||current.status===404;await current.body?.cancel();}
    if(lost&&!accessEnded){accessEnded=true;onAccessDenied?.('管理员查看权限已失效，请返回管理后台重新进入。');}
    return response;
  }) as typeof fetch;
  return {
    request:read,
    async mediaUrl(reference, signal) {
      const query = new URLSearchParams();
      const { fileId, path, imageGenerationReceiptId, imageGenerationOutputId } = reference.target;
      if (imageGenerationReceiptId || imageGenerationOutputId) {
        if (!imageGenerationReceiptId || !imageGenerationOutputId) throw new Error('图片来源不完整');
        query.set('imageGenerationReceiptId', imageGenerationReceiptId);query.set('outputId', imageGenerationOutputId);
      } else if (fileId) {
        query.set('fileId', fileId);
        if (reference.contentRevision) query.set('contentRevision', reference.contentRevision);
      } else if (path) query.set('path', path);
      else throw new Error('媒体来源不完整');
      const response = await read(`${inspectionResourceBase(id)}/media?${query}`, { method: 'GET', credentials: 'same-origin', cache: 'no-store', signal });
      if (!response.ok) throw new Error('只读媒体不可用');
      const value = await response.json() as { url?: unknown; expiresAt?: unknown };
      if (typeof value.url !== 'string') throw new Error('只读媒体地址无效');
      const url = new URL(value.url, origin), expiry = typeof value.expiresAt === 'number' ? value.expiresAt : typeof value.expiresAt === 'string' ? Date.parse(value.expiresAt) : NaN;
      if (url.origin !== origin || url.username || url.password || url.search || url.hash
        || !/^\/api\/admin\/inspection-media\/[A-Za-z0-9_-]+$/.test(url.pathname)
        || !Number.isFinite(expiry) || expiry <= Date.now()) throw new Error('只读媒体授权无效');
      return url.href;
    },
    artifactUrl(path) { return `${inspectionResourceBase(id)}/artifacts?path=${encodeURIComponent(path)}`; },
    figureUrl(receiptId, outputId) { return `${inspectionResourceBase(id)}/figures/${encodeURIComponent(receiptId)}/${encodeURIComponent(outputId)}`; },
  };
}
